import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { 
  MousePointer, 
  Move, 
  RotateCcw, 
  Maximize2,
  Pencil,
  Box,
  CircleDot,
  Layers,
  Ruler,
  Grid3X3,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  ChevronRight,
  ChevronDown,
  MoreHorizontal,
  Save,
  Download,
  Share2,
  Undo,
  Redo,
  ZoomIn,
  ZoomOut,
  Maximize,
  Settings
} from 'lucide-react'
import { Canvas, useThree } from '@react-three/fiber'
import { OrbitControls, Grid, Environment, PerspectiveCamera, Center, Html, TransformControls } from '@react-three/drei'
import { STLLoader } from 'three-stdlib'
import * as THREE from 'three'
import { convertScadToStl } from '@/lib/scadConverter'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import { useCADStore, type Tool } from '@/stores/cadStore'

const tools: { id: Tool; icon: typeof MousePointer; label: string; shortcut: string }[] = [
  { id: 'select', icon: MousePointer, label: 'Select', shortcut: 'V' },
  { id: 'move', icon: Move, label: 'Move', shortcut: 'G' },
  { id: 'rotate', icon: RotateCcw, label: 'Rotate', shortcut: 'R' },
  { id: 'scale', icon: Maximize2, label: 'Scale', shortcut: 'S' },
  { id: 'sketch', icon: Pencil, label: 'Sketch', shortcut: 'K' },
  { id: 'extrude', icon: Box, label: 'Extrude', shortcut: 'E' },
  { id: 'fillet', icon: CircleDot, label: 'Fillet', shortcut: 'F' },
  { id: 'measure', icon: Ruler, label: 'Measure', shortcut: 'M' },
]

function DemoModel() {
  return (
    <group>
      <mesh position={[0, 0.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[2, 1, 1.5]} />
        <meshStandardMaterial color="#6366f1" metalness={0.3} roughness={0.4} />
      </mesh>
      <mesh position={[0, 1.25, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.3, 0.3, 0.5, 32]} />
        <meshStandardMaterial color="#06b6d4" metalness={0.3} roughness={0.4} />
      </mesh>
    </group>
  )
}

function STLModel({ url, activeTool }: { url: string; activeTool: Tool }) {
  const [geometry, setGeometry] = useState<THREE.BufferGeometry | null>(null)
  const [error, setError] = useState<string | null>(null)
  const meshRef = useRef<THREE.Mesh>(null)
  const { camera } = useThree()
  
  // Map tool to transform mode
  const transformMode = activeTool === 'move' ? 'translate' 
    : activeTool === 'rotate' ? 'rotate' 
    : activeTool === 'scale' ? 'scale' 
    : null

  useEffect(() => {
    const loadModel = async () => {
      try {
        let data: ArrayBuffer
        
        if (url.startsWith('data:')) {
          // Handle base64 data URL (STL only - SCAD is pre-converted)
          const base64Match = url.match(/base64,(.+)/)
          if (base64Match && base64Match[1]) {
            const binary = atob(base64Match[1])
            const bytes = new Uint8Array(binary.length)
            for (let i = 0; i < binary.length; i++) {
              bytes[i] = binary.charCodeAt(i)
            }
            data = bytes.buffer
          } else {
            throw new Error('Invalid data URL format')
          }
        } else {
          const response = await fetch(url)
          data = await response.arrayBuffer()
        }

        const loader = new STLLoader()
        const loadedGeometry = loader.parse(data)
        
        loadedGeometry.computeVertexNormals()
        loadedGeometry.center()
        loadedGeometry.computeBoundingBox()

        const box = loadedGeometry.boundingBox!
        const size = new THREE.Vector3()
        box.getSize(size)
        const maxDim = Math.max(size.x, size.y, size.z)
        if (maxDim > 0) {
          const scale = 5 / maxDim
          loadedGeometry.scale(scale, scale, scale)
        }

        setGeometry(loadedGeometry)

        // Adjust camera
        ;(camera as THREE.PerspectiveCamera).position.set(8, 8, 8)
        camera.lookAt(0, 0, 0)
      } catch (err) {
        console.error('Failed to load STL:', err)
        setError(err instanceof Error ? err.message : 'Failed to load model')
      }
    }

    loadModel()
  }, [url, camera])

  if (error) {
    return (
      <Html center>
        <div className="text-red-400 text-sm bg-surface-900 px-4 py-2 rounded-lg">
          Error loading model: {error}
        </div>
      </Html>
    )
  }

  if (!geometry) {
    return (
      <Html center>
        <div className="text-surface-400 text-sm">Loading model...</div>
      </Html>
    )
  }

  return (
    <Center>
      <group>
        {transformMode && meshRef.current ? (
          <TransformControls object={meshRef.current} mode={transformMode} />
        ) : null}
        <mesh ref={meshRef} geometry={geometry} castShadow receiveShadow>
          <meshPhysicalMaterial
            color="#8b5cf6"
            metalness={0.1}
            roughness={0.4}
            clearcoat={0.3}
            clearcoatRoughness={0.25}
          />
        </mesh>
      </group>
    </Center>
  )
}

function Scene({ convertedStlUrl, activeTool }: { convertedStlUrl?: string | null; activeTool: Tool }) {
  const { viewport } = useCADStore()

  return (
    <>
      <PerspectiveCamera makeDefault position={[5, 5, 5]} fov={50} />
      <OrbitControls 
        enableDamping 
        dampingFactor={0.05}
        minDistance={2}
        maxDistance={50}
      />
      
      <ambientLight intensity={0.4} />
      <directionalLight position={[10, 10, 5]} intensity={1} castShadow />
      <directionalLight position={[-5, 5, -5]} intensity={0.3} />
      
      {viewport.gridVisible && (
        <Grid 
          args={[20, 20]} 
          cellSize={1}
          cellThickness={0.5}
          cellColor="#3f3f46"
          sectionSize={5}
          sectionThickness={1}
          sectionColor="#52525b"
          fadeDistance={30}
          fadeStrength={1}
          followCamera={false}
          infiniteGrid
        />
      )}
      
      {viewport.axesVisible && (
        <axesHelper args={[5]} />
      )}
      
      {convertedStlUrl ? <STLModel url={convertedStlUrl} activeTool={activeTool} /> : <DemoModel />}
      
      <Environment preset="studio" />
    </>
  )
}

interface CADWorkspaceProps {
  designId?: string
  modelUrl?: string
}

export default function CADWorkspace({ designId, modelUrl }: CADWorkspaceProps) {
  const { 
    currentDocument, 
    activeTool, 
    setActiveTool,
    viewport,
    toggleGrid,
    toggleAxes,
    toggleFeatureVisibility
  } = useCADStore()
  
  const [expandedFeatures, setExpandedFeatures] = useState<string[]>(['feat-1'])
  const [convertedStlUrl, setConvertedStlUrl] = useState<string | null>(null)
  const [isConverting, setIsConverting] = useState(false)
  const [conversionError, setConversionError] = useState<string | null>(null)
  const conversionInProgress = useRef(false)

  // Convert SCAD to STL outside of Canvas context (like TextTo3DPage does)
  useEffect(() => {
    if (!modelUrl) {
      setConvertedStlUrl(null)
      setConversionError(null)
      return
    }

    // If it's already an STL URL, use directly
    if (!modelUrl.startsWith('data:text/x-scad')) {
      setConvertedStlUrl(modelUrl)
      return
    }

    // Prevent double conversion in React StrictMode
    if (conversionInProgress.current) return
    conversionInProgress.current = true

    const convert = async () => {
      try {
        setIsConverting(true)
        setConversionError(null)
        
        const base64Match = modelUrl.match(/base64,(.+)/)
        if (!base64Match || !base64Match[1]) {
          throw new Error('Invalid SCAD data URL format')
        }
        const scadCode = atob(base64Match[1])
        console.log('[CADWorkspace] Converting SCAD, length:', scadCode.length)
        
        const result = await convertScadToStl(scadCode)
        
        if (result.success && result.stlUrl) {
          console.log('[CADWorkspace] Conversion successful')
          setConvertedStlUrl(result.stlUrl)
          
          // Save converted STL URL to database so we don't re-convert next time
          if (designId) {
            console.log('[CADWorkspace] Saving converted STL to database...')
            const { error: updateError } = await supabase
              .from('designs')
              .update({ file_path: result.stlUrl })
              .eq('id', designId)
            
            if (updateError) {
              console.error('[CADWorkspace] Failed to save STL to database:', updateError)
            } else {
              console.log('[CADWorkspace] STL saved to database successfully')
            }
          }
        } else {
          throw new Error(result.error || 'Conversion failed')
        }
      } catch (err) {
        console.error('[CADWorkspace] Conversion error:', err)
        setConversionError(err instanceof Error ? err.message : 'Failed to convert')
      } finally {
        setIsConverting(false)
        conversionInProgress.current = false
      }
    }

    convert()
  }, [modelUrl])

  const toggleFeatureExpanded = (id: string) => {
    setExpandedFeatures(prev => 
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    )
  }

  const handleExport = () => {
    if (!convertedStlUrl) {
      console.warn('[CADWorkspace] No model to export')
      return
    }

    try {
      // Extract base64 data from data URL
      const base64Match = convertedStlUrl.match(/base64,(.+)/)
      if (!base64Match || !base64Match[1]) {
        throw new Error('Invalid STL data format')
      }

      // Convert base64 to blob
      const binary = atob(base64Match[1])
      const bytes = new Uint8Array(binary.length)
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i)
      }
      const blob = new Blob([bytes], { type: 'model/stl' })

      // Create download link
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${currentDocument?.name || 'model'}.stl`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      console.log('[CADWorkspace] Model exported successfully')
    } catch (err) {
      console.error('[CADWorkspace] Export failed:', err)
    }
  }

  return (
    <div className="h-full flex">
      {/* Left Toolbar */}
      <div className="w-14 bg-surface-900 border-r border-surface-800 flex flex-col py-2">
        <div className="flex-1 space-y-1 px-2">
          {tools.map((tool) => (
            <button
              key={tool.id}
              onClick={() => setActiveTool(tool.id)}
              className={cn(
                'w-full aspect-square rounded-lg flex items-center justify-center transition-all group relative',
                activeTool === tool.id 
                  ? 'bg-primary-600 text-white' 
                  : 'text-surface-400 hover:bg-surface-800 hover:text-surface-200'
              )}
              title={`${tool.label} (${tool.shortcut})`}
            >
              <tool.icon className="w-5 h-5" />
              <div className="absolute left-full ml-2 px-2 py-1 bg-surface-800 rounded text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                {tool.label}
                <span className="ml-2 text-surface-400">{tool.shortcut}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="px-2 pt-2 border-t border-surface-800 space-y-1">
          <button
            onClick={toggleGrid}
            className={cn(
              'w-full aspect-square rounded-lg flex items-center justify-center transition-colors',
              viewport.gridVisible ? 'text-primary-400' : 'text-surface-500 hover:text-surface-300'
            )}
            title="Toggle Grid"
          >
            <Grid3X3 className="w-5 h-5" />
          </button>
          <button
            onClick={toggleAxes}
            className={cn(
              'w-full aspect-square rounded-lg flex items-center justify-center transition-colors',
              viewport.axesVisible ? 'text-primary-400' : 'text-surface-500 hover:text-surface-300'
            )}
            title="Toggle Axes"
          >
            <Layers className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main Viewport */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <div className="h-12 bg-surface-900/80 backdrop-blur border-b border-surface-800 flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white">{currentDocument?.name || 'Untitled'}</span>
            {currentDocument && (
              <span className="text-xs text-surface-500">
                Last saved {currentDocument.modified.toLocaleTimeString()}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center border-r border-surface-700 pr-2 mr-2">
              <button className="p-2 rounded-lg text-surface-400 hover:text-white hover:bg-surface-800 transition-colors">
                <Undo className="w-4 h-4" />
              </button>
              <button className="p-2 rounded-lg text-surface-400 hover:text-white hover:bg-surface-800 transition-colors">
                <Redo className="w-4 h-4" />
              </button>
            </div>
            <Button variant="ghost" size="sm" onClick={handleExport} disabled={!convertedStlUrl}>
              <Download className="w-4 h-4" />
              Export
            </Button>
            <Button variant="ghost" size="sm">
              <Share2 className="w-4 h-4" />
              Share
            </Button>
            <Button variant="primary" size="sm">
              <Save className="w-4 h-4" />
              Save
            </Button>
          </div>
        </div>

        {/* 3D Viewport */}
        <div className="flex-1 relative viewport-3d">
          {isConverting ? (
          <div className="flex-1 flex flex-col items-center justify-center bg-surface-900">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
            </div>
            <p className="mt-4 text-sm text-surface-400">Converting SCAD to 3D mesh...</p>
          </div>
        ) : conversionError ? (
          <div className="flex-1 flex flex-col items-center justify-center bg-surface-900 p-8">
            <div className="text-center max-w-md">
              <p className="text-red-400 mb-2">Model conversion failed</p>
              <p className="text-surface-400 text-sm mb-4">
                {conversionError.includes('Table') || conversionError.includes('address') 
                  ? 'This AI-generated model is too complex for browser-based rendering. Try regenerating with a simpler prompt.'
                  : conversionError}
              </p>
              <button 
                onClick={() => {
                  setConversionError(null)
                  conversionInProgress.current = false
                }}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : (
          <>
            <Canvas shadows>
              <Scene convertedStlUrl={convertedStlUrl} activeTool={activeTool} />
            </Canvas>

            {/* Viewport Controls */}
            <div className="absolute bottom-4 left-4 flex items-center gap-2">
              <div className="glass rounded-lg flex items-center">
                <button className="p-2 text-surface-400 hover:text-white transition-colors">
                  <ZoomOut className="w-4 h-4" />
                </button>
                <span className="px-2 text-xs text-surface-300 border-x border-surface-700">100%</span>
                <button className="p-2 text-surface-400 hover:text-white transition-colors">
                  <ZoomIn className="w-4 h-4" />
                </button>
              </div>
              <button className="glass p-2 rounded-lg text-surface-400 hover:text-white transition-colors">
                <Maximize className="w-4 h-4" />
              </button>
            </div>

            {/* View Cube Placeholder */}
            <div className="absolute top-4 right-4 w-20 h-20 glass rounded-xl flex items-center justify-center">
              <div className="w-12 h-12 border-2 border-surface-500 rounded transform rotate-12">
                <div className="absolute inset-0 flex items-center justify-center text-xs text-surface-400">
                  Front
                </div>
              </div>
            </div>
          </>
        )}
        </div>
      </div>

      {/* Right Panel - Feature Tree */}
      <div className="w-64 bg-surface-900 border-l border-surface-800 flex flex-col">
        <div className="px-4 py-3 border-b border-surface-800 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">Feature Tree</h3>
          <button className="p-1 rounded hover:bg-surface-800 transition-colors">
            <Settings className="w-4 h-4 text-surface-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {currentDocument?.features.map((feature) => (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              className="mb-1"
            >
              <div
                className={cn(
                  'flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors',
                  'hover:bg-surface-800'
                )}
              >
                <button
                  onClick={() => toggleFeatureExpanded(feature.id)}
                  className="p-0.5 text-surface-500"
                >
                  {expandedFeatures.includes(feature.id) ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                
                <Box className="w-4 h-4 text-primary-400" />
                
                <span className="flex-1 text-sm text-surface-200 truncate">
                  {feature.name}
                </span>
                
                <button
                  onClick={() => toggleFeatureVisibility(feature.id)}
                  className="p-1 rounded hover:bg-surface-700 transition-colors"
                >
                  {feature.visible ? (
                    <Eye className="w-3.5 h-3.5 text-surface-400" />
                  ) : (
                    <EyeOff className="w-3.5 h-3.5 text-surface-600" />
                  )}
                </button>
                
                <button className="p-1 rounded hover:bg-surface-700 transition-colors">
                  {feature.locked ? (
                    <Lock className="w-3.5 h-3.5 text-warning-500" />
                  ) : (
                    <Unlock className="w-3.5 h-3.5 text-surface-600" />
                  )}
                </button>
                
                <button className="p-1 rounded hover:bg-surface-700 transition-colors">
                  <MoreHorizontal className="w-3.5 h-3.5 text-surface-500" />
                </button>
              </div>
              
              {expandedFeatures.includes(feature.id) && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  className="ml-6 pl-4 border-l border-surface-800"
                >
                  <div className="py-2 space-y-1">
                    {Object.entries(feature.parameters).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between text-xs">
                        <span className="text-surface-500 capitalize">{key}</span>
                        <span className="text-surface-300">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Properties Panel */}
        <div className="border-t border-surface-800 p-4">
          <h4 className="text-xs font-semibold text-surface-500 uppercase tracking-wider mb-3">
            Properties
          </h4>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-surface-400 block mb-1">Position</label>
              <div className="grid grid-cols-3 gap-1">
                {['X', 'Y', 'Z'].map((axis) => (
                  <div key={axis} className="flex items-center gap-1">
                    <span className="text-xs text-surface-500">{axis}</span>
                    <input
                      type="text"
                      defaultValue="0"
                      className="w-full px-2 py-1 bg-surface-800 border border-surface-700 rounded text-xs text-white text-right focus:outline-none focus:border-primary-500"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-surface-400 block mb-1">Dimensions</label>
              <div className="grid grid-cols-3 gap-1">
                {['W', 'H', 'D'].map((dim) => (
                  <div key={dim} className="flex items-center gap-1">
                    <span className="text-xs text-surface-500">{dim}</span>
                    <input
                      type="text"
                      defaultValue={dim === 'W' ? '20' : dim === 'H' ? '10' : '15'}
                      className="w-full px-2 py-1 bg-surface-800 border border-surface-700 rounded text-xs text-white text-right focus:outline-none focus:border-primary-500"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
